import React, { useState } from 'react';
import { Form, Button, Alert, Modal } from 'react-bootstrap';
import Select from 'react-select';
import NumberFormat from 'react-number-format';
import Swal from 'sweetalert2';
import {
  IconSparkles,
  IconForms,
  IconBrandWhatsapp,
  IconMail,
  IconPhoto,
  IconAlarm,
  IconAlertCircle,
  IconExternalLink,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { SCHEDULING_FIELD_OPTIONS, SMART_BODY_MODELS } from '../../../constants';
import AgentsFlowService from '../../../services/AgentsFlowService';
import getAvailableFields from '../../utils/getAvailableFields';
import AnalyzeAiResponseModal from './AnalyzeAiResponseModal';
import ExtractVariablesEditor from './ExtractVariablesEditor';

const AiResponseFields = ({ localConfig, handleConfigChange, agentId, nodeId }) => {
  const [analyzeState, setAnalyzeState] = useState({
    open: false,
    loading: false,
    error: null,
    analysis: null,
  });

  const runAnalyze = async () => {
    setAnalyzeState({ open: true, loading: true, error: null, analysis: null });
    try {
      const resp = await AgentsFlowService.analyzeAiResponseInstructions(agentId, {
        node_id: nodeId,
        current_instructions: localConfig.instructions || '',
      });
      setAnalyzeState({ open: true, loading: false, error: null, analysis: resp.data });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'Erro ao analisar. Tente novamente.';
      setAnalyzeState({ open: true, loading: false, error: msg, analysis: null });
    }
  };

  const applyResult = (newInstructions) => {
    handleConfigChange('instructions', newInstructions);
    setAnalyzeState((s) => ({ ...s, open: false }));
  };

  const canAnalyze =
    !!agentId &&
    !!nodeId &&
    (localConfig.instructions || '').length >= 10 &&
    !analyzeState.loading;

  return (
    <>
      <div className="afc-capability-description mb-3">
        <small className="text-muted">
          <IconSparkles size={14} className="me-1" />
          Gera uma resposta inteligente usando IA, considerando o contexto
          da conversa e a base de conhecimento do agente.
        </small>
      </div>
      <Form.Group className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="afc-form-label mb-0">Instruções para a IA (opcional)</Form.Label>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={runAnalyze}
            disabled={!canAnalyze}
            title={
              !agentId
                ? 'Salve o agente primeiro'
                : !nodeId
                ? 'ID do nó não disponível'
                : (localConfig.instructions || '').length < 10
                ? 'Digite pelo menos 10 caracteres'
                : 'Analisar e melhorar com IA'
            }
          >
            <IconSparkles size={13} className="me-1" />
            Analisar e Melhorar
          </Button>
        </div>
        <Form.Control
          as="textarea"
          rows={4}
          className="afc-form-control"
          value={localConfig.instructions || ''}
          onChange={(e) => handleConfigChange('instructions', e.target.value)}
          placeholder="Ex: Responda de forma amigável e sugira produtos relacionados. Foque em ajudar o cliente a encontrar a melhor opção..."
        />
        <Form.Text className="afc-form-hint">
          Instruções adicionais para guiar a resposta. Suporta variáveis: {'{{name}}'}, {'{{user_response}}'}
        </Form.Text>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="use_knowledge_base"
          label="Usar Base de Conhecimento (RAG)"
          checked={localConfig.use_knowledge_base !== false}
          onChange={(e) => handleConfigChange('use_knowledge_base', e.target.checked)}
        />
        <Form.Text className="afc-form-hint">
          Busca informações relevantes nos documentos treinados
        </Form.Text>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="use_conversation_history"
          label="Considerar Histórico da Conversa"
          checked={localConfig.use_conversation_history !== false}
          onChange={(e) => handleConfigChange('use_conversation_history', e.target.checked)}
        />
        <Form.Text className="afc-form-hint">
          Inclui mensagens anteriores para manter contexto
        </Form.Text>
      </Form.Group>
      <hr className="afc-divider" />

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          id="ai-wait-for-response"
          label="Aguardar resposta do cliente"
          checked={localConfig.wait_for_response ?? true}
          onChange={(e) => handleConfigChange('wait_for_response', e.target.checked)}
        />
        <Form.Text className="afc-form-hint">
          Pausa o fluxo após a resposta da IA e aguarda a resposta do usuário
        </Form.Text>
      </Form.Group>
      {(localConfig.wait_for_response ?? true) && (
        <Form.Group className="mb-3">
          <Form.Label className="afc-form-label">Salvar resposta como</Form.Label>
          <Form.Control
            type="text"
            className="afc-form-control"
            value={localConfig.save_response_as || 'user_response'}
            onChange={(e) => handleConfigChange('save_response_as', e.target.value)}
            placeholder="user_response"
          />
          <Form.Text className="afc-form-hint">
            Nome da variável para acessar a resposta: {'{{user_response}}'}
          </Form.Text>
        </Form.Group>
      )}

      <hr className="afc-divider" />
      <ExtractVariablesEditor
        variables={localConfig.extract_variables || []}
        onChange={(v) => handleConfigChange('extract_variables', v)}
      />

      <AnalyzeAiResponseModal
        isOpen={analyzeState.open}
        onClose={() => setAnalyzeState((s) => ({ ...s, open: false }))}
        onApply={applyResult}
        currentInstructions={localConfig.instructions || ''}
        analysis={analyzeState.analysis}
        loading={analyzeState.loading}
        error={analyzeState.error}
        onRetry={runAnalyze}
      />
    </>
  );
};

/**
 * ActionFields - Renders configuration fields for action node types.
 *
 * Handles: send_message, api_call, set_variable, wait_for_input,
 *          ai_response, collect_field, send_whatsapp, send_email, send_media
 */
const ActionFields = ({
  nodeKey,
  nodeId,
  localConfig,
  handleConfigChange,
  nodes,
  agentId,
  integrations,
  mediaList,
  mediaLoading,
  renderDefaultPlaceholder,
}) => {
  switch (nodeKey) {
    case 'send_message':
      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Mensagem</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              className="afc-form-control"
              value={localConfig.message || ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              placeholder="Digite a mensagem que será enviada..."
            />
            <Form.Text className="afc-form-hint">
              Use {'{{name}}'}, {'{{telefone}}'} para dados dinâmicos
            </Form.Text>
          </Form.Group>
          {/* <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="typing-delay"
              label="Simular digitação"
              checked={localConfig.simulateTyping ?? true}
              onChange={(e) => handleConfigChange('simulateTyping', e.target.checked)}
            />
          </Form.Group> */}

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="wait-for-response"
              label="Aguardar resposta do cliente"
              checked={localConfig.wait_for_response ?? true}
              onChange={(e) => handleConfigChange('wait_for_response', e.target.checked)}
            />
            <Form.Text className="afc-form-hint">
              Pausa o fluxo após enviar a mensagem e aguarda a resposta do usuário
            </Form.Text>
          </Form.Group>
          {(localConfig.wait_for_response ?? true) && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Salvar resposta como</Form.Label>
              <Form.Control
                type="text"
                className="afc-form-control"
                value={localConfig.save_response_as || 'user_response'}
                onChange={(e) => handleConfigChange('save_response_as', e.target.value)}
                placeholder="user_response"
              />
              <Form.Text className="afc-form-hint">
                Nome da variável para acessar a resposta: {'{{user_response}}'}
              </Form.Text>
            </Form.Group>
          )}
        </>
      );

    case 'transfer_to_human':
      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Mensagem ao cliente</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="afc-form-control"
              value={localConfig.message ?? ''}
              onChange={(e) => handleConfigChange('message', e.target.value)}
              placeholder="Vou te transferir para um de nossos atendentes. Só um momento, por favor."
            />
            <Form.Text className="afc-form-hint">
              Enviada ao cliente antes da IA pausar. Use {'{{name}}'}, {'{{telefone}}'} para dados dinâmicos.
              Se deixar em branco, <strong>nada é enviado ao cliente</strong> (transferência silenciosa).
            </Form.Text>
          </Form.Group>

          <div
            className="p-2 rounded"
            style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3', fontSize: 13 }}
          >
            <strong>Como funciona:</strong> ao chegar neste nó, a IA envia a mensagem acima,
            notifica sua equipe (plataforma e WhatsApp) e para de responder este cliente até
            um atendente assumir. A transferência é <strong>imediata</strong>, independente do horário.
            <br />
            Os números de WhatsApp e o departamento para notificação vêm das configurações de
            <strong> Transferência para Humano</strong> do agente.
          </div>
        </>
      );

    case 'api_call': {
      const availableFields = getAvailableFields(nodes);
      const allFieldOptions = availableFields.flatMap(g => g.options);
      const selectedFieldOptions = (localConfig.bodyFields || []).map(bf =>
        allFieldOptions.find(o => o.value === bf.field) || { value: bf.field, label: bf.field }
      );

      const handleFieldSelection = (selected) => {
        const selectedValues = selected ? selected.map(s => s.value) : [];
        const existing = localConfig.bodyFields || [];
        // Keep existing mappings for fields still selected, add new ones
        const newBodyFields = selectedValues.map(val => {
          const existingField = existing.find(bf => bf.field === val);
          return existingField || { field: val, apiKey: val };
        });
        handleConfigChange('bodyFields', newBodyFields);
      };

      const updateFieldMapping = (index, newApiKey) => {
        const updated = [...(localConfig.bodyFields || [])];
        updated[index] = { ...updated[index], apiKey: newApiKey };
        handleConfigChange('bodyFields', updated);
      };

      const removeField = (index) => {
        const updated = (localConfig.bodyFields || []).filter((_, i) => i !== index);
        handleConfigChange('bodyFields', updated);
      };

      return (
        <>
          {/* URL do Endpoint */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label d-flex align-items-center gap-2">
              URL do Endpoint
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--primary, #4f46e5)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
                title="Como usar variáveis na URL?"
                onClick={() => handleConfigChange('_showEndpointHelp', true)}
              >
                i
              </span>
            </Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.endpoint || ''}
              onChange={(e) => handleConfigChange('endpoint', e.target.value)}
              placeholder="https://api.exemplo.com/endpoint"
            />
          </Form.Group>

          {/* Modal de ajuda da URL */}
          <Modal
            show={!!localConfig._showEndpointHelp}
            onHide={() => handleConfigChange('_showEndpointHelp', false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 16 }}>Como montar a URL do Endpoint</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 14 }}>
              <p>
                A <strong>URL do Endpoint</strong> é o endereço da API que você quer chamar.
                É como um link de internet que leva até um sistema externo para buscar ou enviar informações.
              </p>

              <p className="fw-semibold mb-1">URL simples (fixa):</p>
              <p>
                Se a URL é sempre a mesma, basta colar o endereço completo:
              </p>
              <pre className="p-2 bg-light rounded small">https://api.exemplo.com/produtos</pre>

              <p className="fw-semibold mb-1 mt-3">URL com variável (dinâmica):</p>
              <p>
                Você pode usar <strong>dados coletados do cliente</strong> dentro da URL!
                Basta colocar o nome do campo entre <code>{'{{'}</code> e <code>{'}}'}</code>.
              </p>
              <p>
                Por exemplo, imagine que você coletou o <strong>CPF</strong> do cliente
                e quer buscar os dados dele numa API. A URL ficaria assim:
              </p>
              <pre className="p-2 bg-light rounded small">https://api.exemplo.com/clientes?cpf={'{{cpf}}'}</pre>
              <p className="text-muted small">
                Quando o agente executar, o <code>{'{{cpf}}'}</code> será substituído automaticamente
                pelo CPF real que o cliente informou. Se ele digitou <em>123.456.789-00</em>,
                a URL final vira:
              </p>
              <pre className="p-2 bg-light rounded small">https://api.exemplo.com/clientes?cpf=123.456.789-00</pre>

              <p className="fw-semibold mb-1 mt-3">Outros exemplos:</p>
              <div className="small">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr><th>Você quer buscar por...</th><th>URL com variável</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Nome do cliente</td>
                      <td><code>{'https://api.com/busca?nome={{name}}'}</code></td>
                    </tr>
                    <tr>
                      <td>Telefone</td>
                      <td><code>{'https://api.com/clientes/{{phone}}'}</code></td>
                    </tr>
                    <tr>
                      <td>Código do produto</td>
                      <td><code>{'https://api.com/produtos/{{codigo_produto}}'}</code></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="fw-semibold mb-1 mt-3">Limpar dados numéricos:</p>
              <p>
                Muitas APIs exigem que CPF, telefone e outros dados sejam enviados <strong>apenas com números</strong>,
                sem pontos, traços ou parênteses. Para isso, ative a opção <em>"Enviar apenas números"</em> abaixo do campo Método HTTP.
              </p>
              <div className="small">
                <table className="table table-sm table-bordered mb-0">
                  <thead>
                    <tr><th>Dado original</th><th>Enviado para a API</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>CPF: <code>123.456.789-10</code></td>
                      <td><code>12345678910</code></td>
                    </tr>
                    <tr>
                      <td>Telefone: <code>(47) 99136-0752</code></td>
                      <td><code>47991360752</code></td>
                    </tr>
                    <tr>
                      <td>Nome: <code>João Silva</code></td>
                      <td><code>João Silva</code> (mantém original)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted small mt-1">
                Campos que não possuem dígitos (como nomes) são mantidos sem alteração.
              </p>

              <div className="mt-3 p-2 rounded" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                <strong className="small">Dica:</strong>
                <span className="small"> Os nomes das variáveis são os mesmos campos que você configurou nas ações de coleta do seu fluxo (como <em>name</em>, <em>phone</em>, <em>cpf</em>, etc.).</span>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" size="sm" onClick={() => handleConfigChange('_showEndpointHelp', false)}>
                Entendi
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Método HTTP */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Método HTTP</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.method || 'GET'}
              onChange={(e) => handleConfigChange('method', e.target.value)}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </Form.Select>
          </Form.Group>

          {/* Limpar dados numéricos */}
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="strip-non-numeric"
              label="Enviar apenas números (remove pontos, traços, parênteses...)"
              checked={!!localConfig.stripNonNumeric}
              onChange={(e) => handleConfigChange('stripNonNumeric', e.target.checked)}
              className="afc-form-label"
            />
            <Form.Text className="text-muted">
              CPF 123.456.789-10 → 12345678910 | Tel (00) 00000-0000 → 00000000000
            </Form.Text>
          </Form.Group>

          {/* Headers */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Headers (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="afc-form-control"
              value={localConfig.headers || '{}'}
              onChange={(e) => handleConfigChange('headers', e.target.value)}
              placeholder='{"Authorization": "Bearer token"}'
            />
          </Form.Group>

          {/* Body — Smart (IA) */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label d-flex align-items-center gap-2">
              Formato do Body (Smart IA)
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--primary, #4f46e5)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
                title="Como funciona?"
                onClick={() => handleConfigChange('_showBodyHelp', true)}
              >
                i
              </span>
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={6}
              className="afc-form-control mb-2"
              value={localConfig.bodyTemplate || ''}
              onChange={(e) => {
                handleConfigChange('bodyTemplate', e.target.value);
                handleConfigChange('bodyMode', 'smart');
              }}
              placeholder={'Cole o formato que a API espera. Exemplo:\n{\n  "patient": {\n    "name": "nome do paciente",\n    "phone": "+55...",\n    "birth_date": "YYYY-MM-DD"\n  },\n  "appointment": {\n    "date": "2025-03-15",\n    "time": "14:00"\n  }\n}'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <Form.Label className="afc-form-label" style={{ fontSize: 12 }}>Modelo formatador</Form.Label>
            <Form.Select
              size="sm"
              className="afc-form-control"
              value={localConfig.bodyFormatterModel || 'gpt-4.1-mini'}
              onChange={(e) => handleConfigChange('bodyFormatterModel', e.target.value)}
            >
              {SMART_BODY_MODELS.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.recommended ? ' (Recomendado)' : ''}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted" style={{ fontSize: 11 }}>
              Cole o formato JSON que a API espera. A IA preencherá automaticamente com os dados do cliente.
            </Form.Text>
          </Form.Group>

          {/* Modal de ajuda — Smart Body */}
          <Modal
            show={!!localConfig._showBodyHelp}
            onHide={() => handleConfigChange('_showBodyHelp', false)}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 16 }}>Como funciona o envio inteligente de dados</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 14 }}>
              <p>
                Quando o agente faz uma chamada de API (POST, PUT ou PATCH), ele precisa enviar dados
                no <strong>body</strong> da requisição. O <strong>Smart Body</strong> usa IA para montar
                esse body automaticamente — você só precisa colar o <strong>formato que a API espera</strong>.
              </p>

              <p className="fw-semibold mb-1">Como funciona, passo a passo:</p>
              <ol className="mb-3">
                <li className="mb-2">
                  <strong>Você cola o formato/template</strong> — Copie um exemplo de body da
                  documentação da API e cole no campo. Pode ser um JSON simples ou com objetos
                  aninhados, arrays, datas, etc. Não precisa preencher com dados reais, basta
                  mostrar a <em>estrutura</em>.
                </li>
                <li className="mb-2">
                  <strong>A IA coleta os dados disponíveis</strong> — No momento da execução,
                  a IA reúne automaticamente todos os dados que o agente já tem: dados coletados
                  do cliente (nome, telefone, email, etc.), variáveis definidas no fluxo,
                  respostas de APIs anteriores e a data/hora atual.
                </li>
                <li className="mb-2">
                  <strong>A IA preenche o template</strong> — Um modelo de IA leve (rápido e barato)
                  analisa o template e os dados disponíveis, e gera o JSON final preenchido
                  corretamente, respeitando tipos, formatos de data, objetos aninhados, etc.
                </li>
                <li>
                  <strong>O body é enviado para a API</strong> — O JSON gerado pela IA é usado
                  como body da requisição HTTP.
                </li>
              </ol>

              <p className="fw-semibold mb-1">Exemplo prático:</p>
              <p className="mb-1">
                Suponha que a API de agendamento espera este formato:
              </p>
              <pre className="p-2 bg-light rounded small">
{`{
  "patient": {
    "full_name": "patient name",
    "phone": "+5511999999999",
    "birth_date": "1990-05-20"
  },
  "appointment": {
    "date": "2025-03-15",
    "time": "14:00",
    "specialty": "cardiology"
  }
}`}
              </pre>
              <p className="mb-1">
                Você <strong>cola exatamente isso</strong> no campo. Se o agente já coletou do cliente
                os dados <code>name</code>, <code>phone</code>, <code>birth_date</code> e o cliente
                escolheu uma data e horário, a IA vai gerar algo como:
              </p>
              <pre className="p-2 bg-light rounded small">
{`{
  "patient": {
    "full_name": "Maria Silva",
    "phone": "+5511987654321",
    "birth_date": "1990-05-20"
  },
  "appointment": {
    "date": "2025-03-15",
    "time": "14:00",
    "specialty": "cardiology"
  }
}`}
              </pre>

              <p className="fw-semibold mb-1">Quais dados a IA tem acesso?</p>
              <ul className="mb-3">
                <li><strong>Dados coletados</strong> — Tudo que foi coletado via coleta de dados (nome, email, CPF, etc.)</li>
                <li><strong>Nome e telefone do cliente</strong> — Informações básicas do contato</li>
                <li><strong>Respostas de APIs anteriores</strong> — Dados retornados por outras chamadas de API no fluxo</li>
                <li><strong>Data e hora atual</strong> — Para preencher campos de data/hora corretamente</li>
              </ul>

              <p className="fw-semibold mb-1">Dicas:</p>
              <ul className="mb-0 text-muted">
                <li>Use valores descritivos no template (ex: <code>"nome do paciente"</code>) para que a IA entenda o que colocar em cada campo.</li>
                <li>Se um campo tem valor fixo (ex: <code>"tipo": "agendamento"</code>), coloque o valor real — a IA vai manter.</li>
                <li>Se a IA não encontrar um dado, ela deixa o campo vazio (<code>""</code>) ou <code>null</code>.</li>
                <li>O modelo <strong>GPT-4.1 Mini</strong> é recomendado por ser rápido, preciso e de baixo custo.</li>
              </ul>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" size="sm" onClick={() => handleConfigChange('_showBodyHelp', false)}>
                Entendi
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Salvar resposta em */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label d-flex align-items-center gap-2">
              Salvar resposta em
              <span
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--primary, #4f46e5)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
                title="Como usar a resposta da API?"
                onClick={() => handleConfigChange('_showResponseVarHelp', true)}
              >
                i
              </span>
            </Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.responseVariable || ''}
              onChange={(e) => handleConfigChange('responseVariable', e.target.value)}
              placeholder="api_response"
            />
          </Form.Group>

          {/* Modal de ajuda — Salvar resposta em */}
          <Modal
            show={!!localConfig._showResponseVarHelp}
            onHide={() => handleConfigChange('_showResponseVarHelp', false)}
            centered
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 16 }}>Como usar a resposta da API em outros nós</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 14 }}>
              <p>
                Quando a chamada de API é executada, a resposta recebida é <strong>salva automaticamente
                em uma variável</strong> com o nome que você definir neste campo. Essa variável fica
                disponível para todo o restante do fluxo.
              </p>

              <p className="fw-semibold mb-1">Como funciona:</p>
              <ol className="mb-3">
                <li className="mb-2">
                  <strong>Você define o nome da variável</strong> — Por exemplo, se você digitar
                  <code> pedido</code>, a resposta da API será salva na variável <code>pedido</code>.
                </li>
                <li className="mb-2">
                  <strong>A IA interpreta a resposta</strong> — O <em>Prompt de interpretação</em> (campo abaixo)
                  instrui a IA sobre como apresentar os dados da resposta ao cliente neste mesmo nó.
                  Por exemplo: <em>"Informe ao cliente o status do pedido e o prazo de entrega."</em>
                </li>
                <li className="mb-2">
                  <strong>Use em outros nós do fluxo</strong> — Em qualquer nó posterior (mensagem,
                  condição, outra chamada de API, etc.), você pode acessar os dados usando a
                  sintaxe <code>{`{{nome_da_variavel}}`}</code>.
                </li>
              </ol>

              <p className="fw-semibold mb-1">Exemplo prático:</p>
              <p className="mb-1">
                Suponha que você salvou a resposta como <code>pedido</code> e a API retornou:
              </p>
              <pre className="p-2 bg-light rounded small">
{`{
  "order_id": "PED-12345",
  "status": "shipped",
  "tracking_code": "BR123456789",
  "estimated_delivery": "2025-03-20"
}`}
              </pre>

              <p className="fw-semibold mb-1">Onde você pode usar essa variável:</p>
              <ul className="mb-3">
                <li className="mb-2">
                  <strong>Nó de Mensagem:</strong> Escreva no texto da mensagem
                  <code>{` {{pedido}} `}</code> e o agente terá acesso a todos os dados retornados
                  para compor a resposta ao cliente.
                </li>
                <li className="mb-2">
                  <strong>Nó de Condição:</strong> Use a variável para criar bifurcações no fluxo.
                  Por exemplo, verificar se o status do pedido é <em>"shipped"</em> ou <em>"pending"</em>
                  para seguir caminhos diferentes.
                </li>
                <li className="mb-2">
                  <strong>Outra chamada de API:</strong> Use <code>{`{{pedido}}`}</code> no template
                  do body para enviar dados da resposta anterior para outra API.
                </li>
                <li className="mb-2">
                  <strong>Nó de Capacidade (IA):</strong> A IA terá acesso automático à variável e
                  poderá usá-la para responder perguntas do cliente sobre os dados retornados.
                </li>
              </ul>

              <div className="p-2 bg-light rounded small">
                <p className="fw-semibold mb-1">Dica importante:</p>
                <p className="mb-0 text-muted">
                  Escolha nomes descritivos para suas variáveis. Em vez de <code>api_response</code>,
                  use nomes como <code>pedido</code>, <code>agendamento</code>, <code>consulta_cep</code>
                  — assim fica mais fácil lembrar o que cada variável contém quando for usá-la
                  em outros nós.
                </p>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" size="sm" onClick={() => handleConfigChange('_showResponseVarHelp', false)}>
                Entendi
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Timeout */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Timeout (segundos)</Form.Label>
            <Form.Control
              type="number"
              className="afc-form-control"
              value={localConfig.timeout ?? 30}
              onChange={(e) => handleConfigChange('timeout', e.target.value)}
              onBlur={(e) => {
                const val = parseInt(e.target.value);
                handleConfigChange('timeout', isNaN(val) ? 30 : Math.min(120, Math.max(5, val)));
              }}
              min={10}
              max={60}
            />
            <Form.Text className="text-muted">Entre 10 e 60 segundos</Form.Text>
          </Form.Group>

          {/* Máximo de itens enviados à IA */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Máximo de itens enviados à IA</Form.Label>
            <Form.Control
              type="number"
              className="afc-form-control"
              value={localConfig.maxResults ?? 50}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (e.target.value === '') { handleConfigChange('maxResults', ''); return; }
                if (!isNaN(val)) handleConfigChange('maxResults', Math.min(100, val));
              }}
              onBlur={(e) => {
                const val = parseInt(e.target.value);
                handleConfigChange('maxResults', isNaN(val) ? 50 : Math.min(100, Math.max(1, val)));
              }}
              min={1}
              max={100}
            />
            <Form.Text className="text-muted">
              Quando a API retorna uma lista, limita quantos itens são enviados ao modelo de IA (padrão: 50)
            </Form.Text>
          </Form.Group>

          {/* Prompt de interpretação */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Prompt de interpretação da resposta</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="afc-form-control"
              value={localConfig.responsePrompt || ''}
              onChange={(e) => handleConfigChange('responsePrompt', e.target.value)}
              placeholder="Informe ao cliente o status do pedido. Se o campo 'status' for 'enviado', informe o código de rastreio."
            />
            <Form.Text className="text-muted">Como a IA deve responder com os dados recebidos da API</Form.Text>
          </Form.Group>

          {/* Mensagem de erro */}
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Mensagem de erro (fallback)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="afc-form-control"
              value={localConfig.errorMessage ?? 'No momento não foi possível consultar o sistema. Posso ajudar de outra forma?'}
              onChange={(e) => handleConfigChange('errorMessage', e.target.value)}
            />
            <Form.Text className="text-muted">Mensagem enviada ao cliente se a API falhar</Form.Text>
          </Form.Group>

          {/* Botão de teste da API */}
          <div className="d-flex justify-content-end mt-3 mb-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!localConfig.endpoint}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: '6px 20px',
                borderRadius: 6,
                boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                letterSpacing: 0.3,
              }}
              onClick={async () => {
                if (!agentId) {
                  Swal.fire({
                    icon: 'warning',
                    title: 'Salve o agente primeiro',
                    text: 'É necessário salvar o agente antes de testar a chamada de API.',
                    confirmButtonText: 'Entendi',
                    confirmButtonColor: '#3b82f6',
                  });
                  return;
                }

                Swal.fire({
                  title: 'Testando chamada de API...',
                  html: 'Gerando body com dados mock e enviando para o endpoint...',
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  didOpen: () => Swal.showLoading(),
                });

                try {
                  const res = await AgentsFlowService.testApiCall(agentId, {
                    endpoint: localConfig.endpoint || '',
                    method: localConfig.method || 'POST',
                    headers: localConfig.headers || '{}',
                    bodyTemplate: localConfig.bodyTemplate || '',
                    bodyFormatterModel: localConfig.bodyFormatterModel || 'gpt-4.1-mini',
                    timeout: localConfig.timeout || 30,
                  });

                  const data = res.data;

                  if (data.error_type === 'api_key_missing') {
                    Swal.fire({
                      icon: 'warning',
                      title: 'Chave de API não configurada',
                      html: `<p style="text-align:left;font-size:14px">${data.error}</p>
                        <p style="text-align:left;font-size:13px;color:#666">
                          Acesse <strong>Configurações da Conta</strong> para adicionar sua chave de API.
                        </p>`,
                      confirmButtonText: 'Entendi',
                      confirmButtonColor: '#f59e0b',
                    });
                    return;
                  }

                  if (!data.success) {
                    Swal.fire({
                      icon: 'error',
                      title: 'Erro no teste',
                      html: `<p style="text-align:left;font-size:14px">${data.format_error || data.api_error || data.error || 'Erro desconhecido'}</p>`,
                      confirmButtonText: 'Fechar',
                      confirmButtonColor: '#3b82f6',
                    });
                    return;
                  }

                  // Montar resultado de sucesso
                  const bodyPreview = data.formatted_body
                    ? JSON.stringify(data.formatted_body, null, 2)
                    : '(vazio)';
                  const responsePreview = data.api_response
                    ? (typeof data.api_response === 'object'
                      ? JSON.stringify(data.api_response, null, 2)
                      : String(data.api_response))
                    : '(sem resposta)';

                  const statusColor = data.status_code >= 200 && data.status_code < 300 ? '#10b981' : '#ef4444';

                  let suggestedPromptHtml = '';
                  if (data.suggested_prompt) {
                    suggestedPromptHtml = `
                      <div style="margin-top:12px;padding:10px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;text-align:left">
                        <div style="font-weight:600;font-size:13px;color:#166534;margin-bottom:6px">
                          Sugestão de Prompt de Interpretação:
                        </div>
                        <div style="font-size:12px;color:#333;white-space:pre-wrap">${data.suggested_prompt}</div>
                        <button id="swal-copy-prompt" style="margin-top:8px;padding:4px 12px;font-size:11px;background:#16a34a;color:#fff;border:none;border-radius:4px;cursor:pointer">
                          Usar este prompt
                        </button>
                      </div>`;
                  }

                  Swal.fire({
                    icon: 'success',
                    title: 'Teste realizado com sucesso',
                    width: '700px',
                    html: `
                      <div style="text-align:left;font-size:13px;max-height:60vh;overflow-y:auto">
                        <div style="margin-bottom:10px">
                          <strong>Endpoint:</strong> <code style="font-size:12px">${data.method} ${data.endpoint}</code>
                          <span style="margin-left:8px;padding:2px 8px;border-radius:4px;font-size:12px;font-weight:600;color:#fff;background:${statusColor}">
                            ${data.status_code}
                          </span>
                        </div>

                        <div style="margin-bottom:8px">
                          <strong>Body enviado (gerado pela IA):</strong>
                          <pre style="background:#f1f5f9;padding:8px;border-radius:4px;font-size:11px;max-height:200px;overflow:auto;margin-top:4px">${bodyPreview}</pre>
                        </div>

                        <div style="margin-bottom:8px">
                          <strong>Dados mock utilizados:</strong>
                          <pre style="background:#f1f5f9;padding:8px;border-radius:4px;font-size:11px;max-height:150px;overflow:auto;margin-top:4px">${JSON.stringify(data.mock_data_used, null, 2)}</pre>
                        </div>

                        <div>
                          <strong>Resposta da API:</strong>
                          <pre style="background:#f1f5f9;padding:8px;border-radius:4px;font-size:11px;max-height:200px;overflow:auto;margin-top:4px">${responsePreview}</pre>
                        </div>

                        ${suggestedPromptHtml}
                      </div>`,
                    confirmButtonText: 'Fechar',
                    confirmButtonColor: '#3b82f6',
                    didOpen: () => {
                      const btn = document.getElementById('swal-copy-prompt');
                      if (btn) {
                        btn.addEventListener('click', () => {
                          handleConfigChange('responsePrompt', data.suggested_prompt);
                          btn.textContent = 'Aplicado!';
                          btn.style.background = '#059669';
                          btn.disabled = true;
                        });
                      }
                    },
                  });
                } catch (err) {
                  const errMsg = err?.response?.data?.error || err?.message || 'Erro ao testar chamada de API';
                  const errType = err?.response?.data?.error_type;

                  if (errType === 'api_key_missing') {
                    Swal.fire({
                      icon: 'warning',
                      title: 'Chave de API não configurada',
                      html: `<p style="text-align:left;font-size:14px">${errMsg}</p>
                        <p style="text-align:left;font-size:13px;color:#666">
                          Acesse <strong>Configurações da Conta</strong> para adicionar sua chave de API.
                        </p>`,
                      confirmButtonText: 'Entendi',
                      confirmButtonColor: '#f59e0b',
                    });
                  } else {
                    Swal.fire({
                      icon: 'error',
                      title: 'Erro no teste',
                      text: errMsg,
                      confirmButtonText: 'Fechar',
                      confirmButtonColor: '#3b82f6',
                    });
                  }
                }
              }}
            >
              Testar envio
            </Button>
          </div>
        </>
      );
    }

    case 'set_variable':
      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Nome da Variável</Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.variable || ''}
              onChange={(e) => handleConfigChange('variable', e.target.value)}
              placeholder="nome_variavel"
            />
            <Form.Text className="afc-form-hint">
              Sem espaços, use underscore
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Valor</Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.value || ''}
              onChange={(e) => handleConfigChange('value', e.target.value)}
              placeholder="Valor ou {{outra_variavel}}"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Tipo</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.valueType || 'string'}
              onChange={(e) => handleConfigChange('valueType', e.target.value)}
            >
              <option value="string">Texto</option>
              <option value="number">Número</option>
              <option value="boolean">Booleano</option>
              <option value="expression">Expressão</option>
            </Form.Select>
          </Form.Group>
        </>
      );

    case 'wait_for_input':
      return (
        <>
          <div className="afc-capability-description mb-3">
            <small className="text-muted">
              <strong>Temporizador / Lembrete</strong><br/>
              Pausa o fluxo por um tempo definido antes de continuar automaticamente.
              Ideal para: enviar lembretes, follow-ups agendados, ou dar tempo ao cliente para responder.
            </small>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Tempo de Espera</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                className="afc-form-control"
                style={{ width: '100px' }}
                value={localConfig.delay_value || 0}
                min={0}
                onChange={(e) => handleConfigChange('delay_value', parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <Form.Select
                className="afc-form-control"
                style={{ width: '150px' }}
                value={localConfig.delay_unit || 'hours'}
                onChange={(e) => handleConfigChange('delay_unit', e.target.value)}
              >
                <option value="minutes">Minutos</option>
                <option value="hours">Horas</option>
                <option value="days">Dias</option>
              </Form.Select>
            </div>
            <Form.Text className="afc-form-hint">
              {localConfig.delay_value > 0
                ? `O fluxo continuará automaticamente após ${localConfig.delay_value} ${
                    localConfig.delay_unit === 'minutes' ? 'minuto(s)' :
                    localConfig.delay_unit === 'days' ? 'dia(s)' : 'hora(s)'
                  }`
                : 'Deixe em 0 para aguardar resposta do usuário'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Mensagem antes de aguardar (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="afc-form-control"
              value={localConfig.prompt || ''}
              onChange={(e) => handleConfigChange('prompt', e.target.value)}
              placeholder="Ex: Vou te enviar um lembrete em breve..."
            />
            <Form.Text className="afc-form-hint">
              Esta mensagem será enviada imediatamente, antes de iniciar a espera
            </Form.Text>
          </Form.Group>

          {localConfig.delay_value > 0 && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="cancel-on-user-message"
                label="Cancelar se cliente responder"
                checked={localConfig.cancel_on_user_message ?? true}
                onChange={(e) => handleConfigChange('cancel_on_user_message', e.target.checked)}
              />
              <Form.Text className="afc-form-hint">
                Se o cliente enviar mensagem antes do tempo, cancela o lembrete automático
              </Form.Text>
            </Form.Group>
          )}

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Salvar resposta do cliente como</Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.save_response_as || 'user_response'}
              onChange={(e) => handleConfigChange('save_response_as', e.target.value)}
              placeholder="user_response"
            />
            <Form.Text className="afc-form-hint">
              Se o cliente responder, salva em {'{{user_response}}'} para uso posterior
            </Form.Text>
          </Form.Group>
        </>
      );

    case 'ai_response':
      return (
        <AiResponseFields
          localConfig={localConfig}
          handleConfigChange={handleConfigChange}
          agentId={agentId}
          nodeId={nodeId}
        />
      );

    case 'collect_field':
      return (
        <>
          <div className="afc-capability-description mb-3">
            <small className="text-muted">
              <IconForms size={14} className="me-1" />
              Coleta um dado específico do cliente e salva automaticamente no CRM.
              Use para capturar nome, email, CPF, ou qualquer informação personalizada.
            </small>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Campo a Coletar *</Form.Label>
            <Select
              options={SCHEDULING_FIELD_OPTIONS}
              value={
                SCHEDULING_FIELD_OPTIONS.flatMap(g => g.options).find(
                  opt => opt.value === localConfig.field_name
                ) || (localConfig.field_name ? { value: localConfig.field_name, label: localConfig.field_name } : null)
              }
              onChange={(selected) => {
                handleConfigChange('field_name', selected?.value || '');
                // Auto-preencher crm_field com o mesmo valor
                if (!localConfig.crm_field) {
                  handleConfigChange('crm_field', selected?.value || '');
                }
              }}
              placeholder="Selecione ou digite..."
              isClearable
              isSearchable
              classNamePrefix="afc-select"
              noOptionsMessage={() => 'Digite para criar campo personalizado'}
              formatCreateLabel={(inputValue) => `Criar campo: "${inputValue}"`}
            />
            <Form.Text className="afc-form-hint">
              Selecione um campo padrão ou digite um nome personalizado
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Pergunta ao Cliente</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="afc-form-control"
              value={localConfig.prompt || ''}
              onChange={(e) => handleConfigChange('prompt', e.target.value)}
              placeholder="Ex: Qual seu nome completo?"
            />
            <Form.Text className="afc-form-hint">
              Mensagem enviada para solicitar o dado. Deixe vazio para capturar da última resposta.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Validação</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.validation || 'none'}
              onChange={(e) => handleConfigChange('validation', e.target.value)}
            >
              <option value="none">Nenhuma</option>
              <option value="cpf">CPF (valida dígitos)</option>
              <option value="phone">Telefone (formato brasileiro)</option>
              <option value="email">E-mail</option>
              <option value="options">Opções personalizadas</option>
            </Form.Select>
            <Form.Text className="afc-form-hint">
              Se inválido, solicita novamente automaticamente
            </Form.Text>
          </Form.Group>

          {localConfig.validation === 'options' && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Opções Válidas *</Form.Label>
              <Form.Control
                type="text"
                className="afc-form-control"
                value={localConfig.validation_options || ''}
                onChange={(e) => handleConfigChange('validation_options', e.target.value)}
                placeholder="Ex: Paulo, Milene, Carlos"
              />
              <Form.Text className="afc-form-hint">
                Lista de opções válidas separadas por vírgula. O agente só aceitará respostas que correspondam a uma dessas opções.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="field-required"
              label="Campo obrigatório"
              checked={localConfig.required !== false}
              onChange={(e) => handleConfigChange('required', e.target.checked)}
            />
          </Form.Group>

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="sync-crm"
              label="Salvar no CRM automaticamente"
              checked={localConfig.sync_crm !== false}
              onChange={(e) => handleConfigChange('sync_crm', e.target.checked)}
            />
            <Form.Text className="afc-form-hint">
              Sincroniza o dado com o cadastro do contato em tempo real
            </Form.Text>
          </Form.Group>

          {localConfig.sync_crm !== false && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Nome do campo no CRM</Form.Label>
              <Form.Control
                type="text"
                className="afc-form-control"
                value={localConfig.crm_field || localConfig.field_name || ''}
                onChange={(e) => handleConfigChange('crm_field', e.target.value)}
                placeholder="Mesmo nome do campo selecionado"
              />
              <Form.Text className="afc-form-hint">
                Campos padrão (name, phone, tags) vão para o contato. Outros criam propriedades customizadas.
              </Form.Text>
            </Form.Group>
          )}

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="collect-wait-for-response"
              label="Aguardar resposta do cliente"
              checked={localConfig.wait_for_response ?? true}
              onChange={(e) => handleConfigChange('wait_for_response', e.target.checked)}
            />
            <Form.Text className="afc-form-hint">
              Pausa o fluxo após enviar a pergunta e aguarda a resposta do usuário antes de prosseguir
            </Form.Text>
          </Form.Group>
        </>
      );

    case 'send_whatsapp':
      return (
        <>
          <div className="afc-capability-description mb-3">
            <small className="text-muted">
              <IconBrandWhatsapp size={14} className="me-1" />
              Envia uma mensagem WhatsApp para o cliente da conversa ou para números específicos.
            </small>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Destinatário</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.recipient_type || 'client'}
              onChange={(e) => handleConfigChange('recipient_type', e.target.value)}
            >
              <option value="client">Cliente da conversa</option>
              <option value="custom">Números específicos</option>
            </Form.Select>
          </Form.Group>

          {localConfig.recipient_type === 'custom' && (() => {
            const phonesStr = localConfig.phones || '';
            const phonesList = phonesStr ? phonesStr.split(',').map(p => p.trim()) : [''];
            if (phonesList.length === 0) phonesList.push('');

            const updatePhones = (newList) => {
              // Preserva slots vazios no config para manter os inputs no UI.
              // O backend já ignora strings vazias no split.
              handleConfigChange('phones', newList.join(','));
            };

            const handlePhoneChange = (index, rawValue) => {
              const newList = [...phonesList];
              newList[index] = rawValue;
              updatePhones(newList);
            };

            const addPhone = () => {
              if (phonesList.length < 3) updatePhones([...phonesList, '']);
            };

            const removePhone = (index) => {
              const newList = phonesList.filter((_, i) => i !== index);
              updatePhones(newList.length > 0 ? newList : ['']);
            };

            return (
              <Form.Group className="mb-3">
                <Form.Label className="afc-form-label">Números de Telefone</Form.Label>
                {phonesList.map((phone, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mb-2">
                    <NumberFormat
                      format={phone.length >= 3 && phone[2] !== '9' ? "(##) ####-####" : "(##) #####-####"}
                      mask="_"
                      className="form-control afc-form-control"
                      placeholder="(11) 99999-9999"
                      value={phone}
                      onValueChange={(values) => handlePhoneChange(index, values.value)}
                    />
                    {phonesList.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={() => removePhone(index)}
                      >
                        <IconX size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                {phonesList.length < 3 && (
                  <Button variant="outline-secondary" size="sm" onClick={addPhone}>
                    <IconPlus size={14} className="me-1" />
                    Adicionar número ({phonesList.length}/3)
                  </Button>
                )}
                <Form.Text className="afc-form-hint d-block mt-1">
                  Até 3 números. Formato: (DDD) número
                </Form.Text>
              </Form.Group>
            );
          })()}

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Tipo de Mensagem</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.message_type || 'fixed'}
              onChange={(e) => handleConfigChange('message_type', e.target.value)}
            >
              <option value="fixed">Mensagem fixa</option>
              <option value="ai">Gerar com IA</option>
            </Form.Select>
          </Form.Group>

          {(localConfig.message_type || 'fixed') === 'fixed' && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Mensagem</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="afc-form-control"
                value={localConfig.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder="Olá {{name}}, sua solicitação foi recebida!"
              />
              <Form.Text className="afc-form-hint">
                {'Use {{name}}, {{phone}} para dados dinâmicos'}
              </Form.Text>
            </Form.Group>
          )}

          {localConfig.message_type === 'ai' && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Prompt da IA</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="afc-form-control"
                value={localConfig.ai_prompt || ''}
                onChange={(e) => handleConfigChange('ai_prompt', e.target.value)}
                placeholder="Gere uma mensagem de confirmação de agendamento com os dados do cliente"
              />
              <Form.Text className="afc-form-hint">
                A IA usará este prompt junto com os dados da conversa para gerar a mensagem
              </Form.Text>
            </Form.Group>
          )}

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">
              <IconAlarm size={14} className="me-1" />
              Agendar envio
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                className="afc-form-control"
                style={{ width: '80px' }}
                min={0}
                value={localConfig.delay_value || 0}
                onChange={(e) => handleConfigChange('delay_value', parseInt(e.target.value) || 0)}
              />
              <Form.Select
                className="afc-form-control"
                value={localConfig.delay_unit || 'minutes'}
                onChange={(e) => handleConfigChange('delay_unit', e.target.value)}
              >
                <option value="minutes">minutos</option>
                <option value="hours">horas</option>
                <option value="days">dias</option>
              </Form.Select>
            </div>
            <Form.Text className="afc-form-hint">
              Deixe 0 para enviar imediatamente. Com delay, a mensagem é preparada agora e enviada após o tempo definido.
            </Form.Text>
          </Form.Group>
        </>
      );

    case 'send_email': {
      const gmailIntegration = integrations.find(
        (i) => (i.integration_type === 'gmail' || i.integration_type === 'google_workspace') && i.is_active
      );

      return (
        <>
          <div className="afc-capability-description mb-3">
            <small className="text-muted">
              <IconMail size={14} className="me-1" />
              Envia um email via Gmail usando a conta Google conectada do agente.
            </small>
          </div>

          {!gmailIntegration && (
            <Alert variant="warning" className="mb-3 d-flex align-items-center gap-2">
              <IconAlertCircle size={18} />
              <small>
                Conecte uma conta Google com permissão de <strong>Gmail</strong> na aba <strong>Integrações</strong> antes de usar esta ação.
              </small>
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Nome do Remetente <small className="text-muted">(opcional)</small></Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.sender_name || ''}
              onChange={(e) => handleConfigChange('sender_name', e.target.value)}
              placeholder="Ex: Minha Empresa"
            />
            <Form.Text className="afc-form-hint">
              Nome exibido como remetente do email. Se vazio, usa o nome da conta Google.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Destinatário</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.recipient_type || 'client'}
              onChange={(e) => handleConfigChange('recipient_type', e.target.value)}
            >
              <option value="client">Email do cliente (capturado pelo agente)</option>
              <option value="custom">Email personalizado</option>
            </Form.Select>
            <Form.Text className="afc-form-hint">
              {(localConfig.recipient_type || 'client') === 'client'
                ? 'Usa o email coletado durante a conversa (campo "email" dos dados capturados)'
                : 'Informe o email de destino abaixo'}
            </Form.Text>
          </Form.Group>

          {(localConfig.recipient_type || 'client') === 'custom' && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Para</Form.Label>
              <Form.Control
                type="text"
                className="afc-form-control"
                value={localConfig.to || ''}
                onChange={(e) => handleConfigChange('to', e.target.value)}
                placeholder="email@exemplo.com"
              />
              <Form.Text className="afc-form-hint">
                {'Use {{email}} para dados dinâmicos. Separe múltiplos por vírgula.'}
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">CC <small className="text-muted">(opcional)</small></Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.cc || ''}
              onChange={(e) => handleConfigChange('cc', e.target.value)}
              placeholder="copia@exemplo.com"
            />
            <Form.Text className="afc-form-hint">
              Separe múltiplos emails por vírgula
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Assunto</Form.Label>
            <Form.Control
              type="text"
              className="afc-form-control"
              value={localConfig.subject || ''}
              onChange={(e) => handleConfigChange('subject', e.target.value)}
              placeholder="Confirmação de agendamento"
            />
            <Form.Text className="afc-form-hint">
              {'Use {{name}}, {{phone}} para dados dinâmicos'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Tipo de Conteúdo</Form.Label>
            <Form.Select
              className="afc-form-control"
              value={localConfig.message_type || 'fixed'}
              onChange={(e) => handleConfigChange('message_type', e.target.value)}
            >
              <option value="fixed">Mensagem fixa</option>
              <option value="ai">Gerar com IA</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Formato HTML"
              checked={localConfig.is_html !== false}
              onChange={(e) => handleConfigChange('is_html', e.target.checked)}
            />
            <Form.Text className="afc-form-hint">
              Quando ativo, o corpo do email é interpretado como HTML
            </Form.Text>
          </Form.Group>

          {(localConfig.message_type || 'fixed') === 'fixed' && (
            <Form.Group className="mb-3">
              <Form.Label className="afc-form-label">Mensagem</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                className="afc-form-control"
                value={localConfig.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder={'<h2>Olá {{name}}</h2>\n<p>Sua solicitação foi recebida com sucesso.</p>'}
              />
              <Form.Text className="afc-form-hint">
                {'Use {{name}}, {{phone}} para dados dinâmicos. Suporta HTML.'}
              </Form.Text>
            </Form.Group>
          )}

          {localConfig.message_type === 'ai' && (() => {
            const defaultEmailPrompt = 'Gere um email HTML profissional de confirmação para o cliente.\n\nUse os dados coletados na conversa (nome, telefone, data, horário).\nO email deve ter:\n- Saudação personalizada\n- Detalhes do agendamento/solicitação\n- Informações de contato para dúvidas\n- Assinatura profissional\n\nResponda APENAS com o HTML do email, sem explicações.';
            if (!localConfig.ai_prompt) {
              handleConfigChange('ai_prompt', defaultEmailPrompt);
            }
            return (
              <Form.Group className="mb-3">
                <Form.Label className="afc-form-label">Prompt da IA</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  className="afc-form-control"
                  value={localConfig.ai_prompt || defaultEmailPrompt}
                  onChange={(e) => handleConfigChange('ai_prompt', e.target.value)}
                />
                <Form.Text className="afc-form-hint">
                  A IA usará este prompt junto com os dados da conversa para gerar o email
                </Form.Text>
              </Form.Group>
            );
          })()}

          {(localConfig.message_type || 'fixed') === 'fixed' && localConfig.message && (
            <div className="mb-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const safeHtml = localConfig.message.replace(/"/g, '&quot;');
                  Swal.fire({
                    title: 'Preview do Email',
                    html: '<iframe srcdoc="' + safeHtml + '" style="width:100%;height:400px;border:1px solid #ddd;border-radius:4px;" sandbox="allow-same-origin"></iframe>',
                    width: '700px',
                    showConfirmButton: false,
                    showCloseButton: true,
                  });
                }}
              >
                <IconExternalLink size={14} className="me-1" />
                Visualizar Email
              </Button>
            </div>
          )}

          <hr className="afc-divider" />

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">
              <IconAlarm size={14} className="me-1" />
              Agendar envio
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                className="afc-form-control"
                style={{ width: '80px' }}
                min={0}
                value={localConfig.delay_value || 0}
                onChange={(e) => handleConfigChange('delay_value', parseInt(e.target.value) || 0)}
              />
              <Form.Select
                className="afc-form-control"
                value={localConfig.delay_unit || 'minutes'}
                onChange={(e) => handleConfigChange('delay_unit', e.target.value)}
              >
                <option value="minutes">minutos</option>
                <option value="hours">horas</option>
                <option value="days">dias</option>
              </Form.Select>
            </div>
            <Form.Text className="afc-form-hint">
              Deixe 0 para enviar imediatamente. Com delay, o email é preparado agora e enviado após o tempo definido.
            </Form.Text>
          </Form.Group>
        </>
      );
    }

    case 'send_media': {
      const selectedMedia = mediaList.find(m => m.id === localConfig.mediaId);

      return (
        <>
          <div className="afc-capability-description mb-3">
            <small className="text-muted">
              <IconPhoto size={14} className="me-1" />
              Envia uma imagem ou PDF da galeria de mídias do agente.
            </small>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Mídia</Form.Label>
            {mediaLoading ? (
              <div className="text-muted"><small>Carregando mídias...</small></div>
            ) : mediaList.length === 0 ? (
              <Alert variant="warning" className="mb-0 d-flex align-items-center gap-2">
                <IconAlertCircle size={18} />
                <small>
                  Nenhuma mídia na galeria. Faça upload de imagens ou PDFs na aba <strong>Configurações</strong> do agente.
                </small>
              </Alert>
            ) : (
              <>
                <Form.Select
                  className="afc-form-control"
                  value={localConfig.mediaId || ''}
                  onChange={(e) => handleConfigChange('mediaId', e.target.value)}
                >
                  <option value="">Selecione uma mídia...</option>
                  {mediaList.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.media_type === 'image' ? '🖼️' : '📄'} {m.name} ({(m.size / 1024).toFixed(0)}KB)
                    </option>
                  ))}
                </Form.Select>
                {selectedMedia && selectedMedia.media_type === 'image' && selectedMedia.url && (
                  <div className="mt-2">
                    <img
                      src={selectedMedia.url}
                      alt={selectedMedia.name}
                      style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6, border: '1px solid #ddd' }}
                    />
                  </div>
                )}
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Legenda <small className="text-muted">(opcional)</small></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              className="afc-form-control"
              value={localConfig.caption || ''}
              onChange={(e) => handleConfigChange('caption', e.target.value)}
              placeholder={'Olá {{name}}, segue o documento solicitado.'}
            />
            <Form.Text className="afc-form-hint">
              {'Use {{name}}, {{phone}} para dados dinâmicos'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="afc-form-label">Enviar via</Form.Label>
            <div>
              <Form.Check
                inline
                type="checkbox"
                id="provider-whatsapp"
                label="WhatsApp"
                checked={(localConfig.providers || ['whatsapp']).includes('whatsapp')}
                onChange={(e) => {
                  const providers = localConfig.providers || ['whatsapp'];
                  if (e.target.checked) {
                    handleConfigChange('providers', [...providers, 'whatsapp']);
                  } else {
                    handleConfigChange('providers', providers.filter(p => p !== 'whatsapp'));
                  }
                }}
              />
              <Form.Check
                inline
                type="checkbox"
                id="provider-site"
                label="Site"
                checked={(localConfig.providers || []).includes('site')}
                onChange={(e) => {
                  const providers = localConfig.providers || ['whatsapp'];
                  if (e.target.checked) {
                    handleConfigChange('providers', [...providers, 'site']);
                  } else {
                    handleConfigChange('providers', providers.filter(p => p !== 'site'));
                  }
                }}
              />
            </div>
          </Form.Group>

          {(localConfig.providers || ['whatsapp']).includes('whatsapp') && (
            <>
              <hr className="afc-divider" />

              <Form.Group className="mb-3">
                <Form.Label className="afc-form-label">
                  <IconAlarm size={14} className="me-1" />
                  Agendar envio
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    className="afc-form-control"
                    style={{ width: '80px' }}
                    min={0}
                    value={localConfig.delay_value || 0}
                    onChange={(e) => handleConfigChange('delay_value', parseInt(e.target.value) || 0)}
                  />
                  <Form.Select
                    className="afc-form-control"
                    value={localConfig.delay_unit || 'minutes'}
                    onChange={(e) => handleConfigChange('delay_unit', e.target.value)}
                  >
                    <option value="minutes">minutos</option>
                    <option value="hours">horas</option>
                    <option value="days">dias</option>
                  </Form.Select>
                </div>
                <Form.Text className="afc-form-hint">
                  Deixe 0 para enviar imediatamente.
                </Form.Text>
              </Form.Group>
            </>
          )}
        </>
      );
    }

    default:
      return renderDefaultPlaceholder('ação');
  }
};

export default ActionFields;
